import { ConnectEmailMethod, MasterTranslation, ParcelSearchesSource } from '@/constants';
import { useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { ConnectGoogleButton } from '@/modules/pmt/components/ConnectGoogleButton';
import { Divider } from '@/modules/pmt/components/Divider';
import { TrackingWidget } from '@/modules/pmt/components/TrackingWidget';
import {
  useEmailSyncContext,
  useMyParcelsContext,
  useShipmentFiltersContext,
  useShipmentsOverviewContext,
} from '@/modules/pmt/context';
import { useFetchShipments } from '@/modules/pmt/hooks';
import { recordSearchT1, scrollIntoView } from '@/modules/pmt/utils';
import { gtmClickEsyncConnect, gtmClickPlus, gtmConnectEmailSuccess, gtmParcelSearches, gtmViewParcel } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import { useMemo, useState } from 'react';
import PlusIcon from '../../../../../../public/images/icons/add-inner-icon.svg';
import { ParcelListTitle, PlusButton, TitleContainer, TrackingWidgetWrapper } from './ShipmentSearch.styles';

export const ShipmentSearch = () => {
  const { t } = useGlobalState();
  const { logEvent } = useStatsigClient();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { mainTranslation } = useMyParcelsContext();
  const { noShipmentsAdded, setShouldAutoOpenMobileDetails } = useShipmentsOverviewContext();
  const { resetFilterAndSort } = useShipmentFiltersContext();
  const { hasEmailConnected, hasEmailAccounts } = useEmailSyncContext();
  const { parcelListSectionTitleDesktop, parcelListSectionTitleMobile } = mainTranslation;
  const [isExpanded, setIsExpanded] = useState(false);

  const { fetchShipments } = useFetchShipments((successCount) => {
    if (successCount > 1 && !isDesktopPMT) {
      scrollIntoView('recently-added-separator', 'start');
    }
  });

  const shipmentListTitle = useMemo(() => {
    if (isDesktopPMT && parcelListSectionTitleDesktop) {
      return parcelListSectionTitleDesktop;
    }

    if (!isDesktopPMT && parcelListSectionTitleMobile) {
      return parcelListSectionTitleMobile;
    }

    return '';
  }, [isDesktopPMT, parcelListSectionTitleDesktop, parcelListSectionTitleMobile]);

  const isSearchExpanded = noShipmentsAdded || isExpanded;

  const renderConnectGoogleButton = () => {
    if (!hasEmailAccounts || !hasEmailConnected) return null;

    return (
      <>
        <Divider>{t(MasterTranslation.Or)}</Divider>
        <ConnectGoogleButton
          buttonText={t(MasterTranslation.TrackingWidgetConnectGmail)}
          onSuccess={() => gtmConnectEmailSuccess(ConnectEmailMethod.TrackingWidget)}
          handleClick={() => gtmClickEsyncConnect({ method: ConnectEmailMethod.TrackingWidget })}
        />
      </>
    );
  };

  const handleSubmit = (trackingIds: string[]) => {
    resetFilterAndSort();
    fetchShipments(trackingIds);
    recordSearchT1(trackingIds);
    gtmParcelSearches(trackingIds.length, ParcelSearchesSource.PMT);
    logEvent('parcel_searches', trackingIds.length, { source: ParcelSearchesSource.PMT });
    gtmViewParcel();
    setIsExpanded(false);

    // Auto open mobile details view if single shipment is being tracked
    // Ticket: https://parcelperform.atlassian.net/browse/PHOENIX-1163
    const isSingleShipment = trackingIds.length === 1;
    if (isSingleShipment && !isDesktopPMT) {
      setShouldAutoOpenMobileDetails(true);
    }
  };

  return (
    <TrackingWidgetWrapper>
      <TitleContainer>
        <ParcelListTitle isExpanded={isSearchExpanded}>{shipmentListTitle}</ParcelListTitle>
        {!noShipmentsAdded && (
          <PlusButton
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} Add Parcel`}
            isExpanded={isExpanded}
            onClick={() => {
              setIsExpanded(!isExpanded);
              if (!isExpanded) gtmClickPlus();
            }}
          >
            <PlusIcon />
          </PlusButton>
        )}
      </TitleContainer>
      {isExpanded && !noShipmentsAdded && (
        <>
          <TrackingWidget handleSubmit={handleSubmit} />
          {renderConnectGoogleButton()}
        </>
      )}
    </TrackingWidgetWrapper>
  );
};
